import { useState, useCallback, useRef } from "react";
import dayjs from "dayjs";
import axios from "axios";
import { Interaction } from "../types/interactions";
import { getInteractionKey } from "../utils/interactionKey";

interface UseInteractionManagerProps {
  userId: number | undefined;
  selectedDate: dayjs.Dayjs | null;
  existingInteractions: Record<string, any>;
  existingSkippedInteractions: Record<string, any>;
  interactionCategory: string;
}

interface UseInteractionManagerReturn {
  interactions: Record<string, Interaction>;
  setInteractions: React.Dispatch<React.SetStateAction<Record<string, Interaction>>>;
  handleInteractionTypeChange: (
    orderNumber: string,
    type: string,
    contactId: number,
    preInstructions?: string,
    settlementDate?: string | null
  ) => void;
  handleInteractionNoteChange: (
    orderNumber: string,
    note: string,
    contactId: number,
    preInstructions?: string,
    settlementDate?: string | null
  ) => void;
  handlePreInstructionsChange: (
    orderNumber: string,
    instructions: string,
    contactId: number,
    settlementDate?: string | null
  ) => void;
  handleSkipWithReason: (
    orderNumber: string,
    reason: string,
    contactId: number,
    settlementDate?: string | null
  ) => void;
  handleUndoInteraction: (orderNumber: string, contactId: number) => Promise<void>;
  handleClosedConfirmationChange: (
    orderNumber: string,
    isConfirmed: boolean
  ) => void;
  setExistingInteractions: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  setExistingSkippedInteractions: React.Dispatch<React.SetStateAction<Record<string, any>>>;
}

export const useInteractionManager = ({
  userId,
  selectedDate,
  existingInteractions: propExistingInteractions,
  existingSkippedInteractions: propExistingSkippedInteractions,
  interactionCategory
}: UseInteractionManagerProps): UseInteractionManagerReturn => {
  const [interactions, setInteractions] = useState<Record<string, Interaction>>({});
  const [existingInteractions, setExistingInteractions] = useState<Record<string, any>>(propExistingInteractions);
  const [existingSkippedInteractions, setExistingSkippedInteractions] = useState<Record<string, any>>(
    propExistingSkippedInteractions
  );
  const saveTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});
  const noteStateTimeoutRef = useRef<Record<string, NodeJS.Timeout>>({});
  const pendingSaveRef = useRef<Record<string, Promise<number | null>>>({});
  const savedIdsRef = useRef<Record<string, number>>({});

  const saveInteraction = useCallback(
    async (
      interaction: Interaction,
      contactId: number,
      preInteractionInstructions?: string,
      settlementDate?: string | null
    ) => {
      const key = getInteractionKey(interaction.orderNumber, contactId);

      // Wait for any pending save for this key to complete first
      const pendingPromise = pendingSaveRef.current[key];
      if (pendingPromise) {
        await pendingPromise;
      }

      const existingFromDb = existingInteractions[key];

      // Preserve existing pre-instructions if not provided
      const effectivePreInstructions = preInteractionInstructions ?? existingFromDb?.preInteractionInstructions;

      const interactionId = savedIdsRef.current[key] || interaction.id || existingFromDb?.id || 0;

      const payload = {
        interaction: {
          id: interactionId,
          contactId: contactId,
          typeId: interaction.typeId,
          notes: interaction.note,
          preInteractionInstructions: effectivePreInstructions,
          orderNumber: interaction.orderNumber,
          interactionDate: interaction.interactionDate,
          clientCultivatorId: userId,
          interactionCategory: settlementDate ? "Closed" : "Received",
          interactionReasonId: settlementDate ? 6 : 5
        },
        colleagueIds: []
      };

      const savePromise = (async () => {
        try {
          const response = await axios.post("/api/clientphonebook/contactInteractions/Upsert", payload);

          if (response.data && interactionId === 0) {
            // Store the new ID immediately in the ref for future saves
            savedIdsRef.current[key] = response.data;

            setInteractions((prev) => ({
              ...prev,
              [key]: {
                ...prev[key],
                id: response.data
              }
            }));

            // Also update existingInteractions to ensure future changes use the same ID
            setExistingInteractions((prev) => ({
              ...prev,
              [key]: {
                ...payload.interaction,
                id: response.data
              }
            }));
          }
          return interactionId || null;
        } catch (err) {
          return null;
        } finally {
          delete pendingSaveRef.current[key];
        }
      })();

      pendingSaveRef.current[key] = savePromise;
      await savePromise;
    },
    [userId, interactionCategory, existingInteractions]
  );

  const handleInteractionTypeChange = useCallback(
    (
      orderNumber: string,
      type: string,
      contactId: number,
      preInstructions?: string,
      settlementDate?: string | null
    ) => {
      const typeId = type ? parseInt(type) : null;
      const key = getInteractionKey(orderNumber, contactId);
      const existingInteraction = interactions[key];
      const existingFromDb = existingInteractions[key];

      const effectivePreInstructions = preInstructions ?? existingFromDb?.preInteractionInstructions;

      const updatedInteraction: Interaction = {
        id: existingInteraction?.id || existingFromDb?.id,
        orderNumber,
        contactId,
        propertyAddress: "",
        typeId,
        type,
        note: existingInteraction?.note || "",
        interactionDate: new Date().toISOString(),
        clientCultivatorId: userId || 0
      };

      setInteractions((prev) => ({
        ...prev,
        [key]: updatedInteraction
      }));

      if (typeId) {
        const key = getInteractionKey(orderNumber, contactId);
        const timeoutKey = key;

        if (saveTimeoutRef.current[timeoutKey]) {
          clearTimeout(saveTimeoutRef.current[timeoutKey]);
        }

        saveTimeoutRef.current[timeoutKey] = setTimeout(() => {
          saveInteraction(updatedInteraction, contactId, effectivePreInstructions, settlementDate);
          delete saveTimeoutRef.current[timeoutKey];
        }, 1000);
      }
    },
    [interactions, existingInteractions, selectedDate, userId, saveInteraction]
  );

  const handleInteractionNoteChange = useCallback(
    (
      orderNumber: string,
      note: string,
      contactId: number,
      preInstructions?: string,
      settlementDate?: string | null
    ) => {
      const key = getInteractionKey(orderNumber, contactId);
      const existingInteraction = interactions[key];
      const existingFromDb = existingInteractions[key];

      // Preserve existing pre-instructions if not provided
      const effectivePreInstructions = preInstructions ?? existingFromDb?.preInteractionInstructions;

      const typeId = existingInteraction?.typeId ?? existingFromDb?.typeId ?? null;

      const updatedInteraction: Interaction = {
        id: savedIdsRef.current[key] || existingInteraction?.id || existingFromDb?.id,
        orderNumber,
        contactId,
        propertyAddress: "",
        typeId,
        type: existingInteraction?.type || "",
        note,
        interactionDate: existingInteraction?.interactionDate || existingFromDb?.interactionDate || null,
        clientCultivatorId: userId || 0
      };

      if (noteStateTimeoutRef.current[key]) {
        clearTimeout(noteStateTimeoutRef.current[key]);
      }
      noteStateTimeoutRef.current[key] = setTimeout(() => {
        setInteractions((prev) => ({
          ...prev,
          [key]: updatedInteraction
        }));
        delete noteStateTimeoutRef.current[key];
      }, 1000);

      const timeoutKey = key;

      if (saveTimeoutRef.current[timeoutKey]) {
        clearTimeout(saveTimeoutRef.current[timeoutKey]);
      }

      saveTimeoutRef.current[timeoutKey] = setTimeout(() => {
        saveInteraction(updatedInteraction, contactId, effectivePreInstructions, settlementDate);
        delete saveTimeoutRef.current[timeoutKey];
      }, 1000);
    },
    [interactions, existingInteractions, selectedDate, userId, saveInteraction]
  );

  const saveSkippedInteraction = useCallback(
    async (
      orderNumber: string,
      contactId: number,
      skipReason: string,
      preInteractionInstructions?: string,
      settlementDate?: string | null
    ) => {
      const key = getInteractionKey(orderNumber, contactId);
      const existingSkipped = existingSkippedInteractions[key];
      const existingRegular = existingInteractions[key];

      // Preserve pre-instructions from either existing source
      const effectivePreInstructions =
        preInteractionInstructions ??
        existingSkipped?.preInteractionInstructions ??
        existingRegular?.preInteractionInstructions;

      const payload = {
        interaction: {
          id: existingSkipped?.id || existingRegular?.id || 0,
          orderNumber,
          contactId,
          typeId: 10, // Skipped interaction type
          notes: skipReason,
          preInteractionInstructions: effectivePreInstructions,
          interactionDate: new Date().toISOString(),
          clientCultivatorId: userId || 0,
          interactionCategory: settlementDate ? "Closed" : "Received",
          interactionReasonId: settlementDate ? 6 : 5
        },
        colleagueIds: []
      };

      try {
        const response = await axios.post("/api/clientphonebook/contactInteractions/Upsert", payload);

        if (response.data && !existingSkipped && !existingRegular) {
          setExistingSkippedInteractions((prev) => ({
            ...prev,
            [key]: {
              id: response.data,
              orderNumber,
              contactId,
              skipReason,
              preInteractionInstructions: effectivePreInstructions,
              skippedDate: null,
              skippedByUserId: userId || 0
            }
          }));
        }
      } catch (err) {}
    },
    [userId, selectedDate, existingSkippedInteractions, existingInteractions, interactionCategory]
  );

  const handleSkipWithReason = useCallback(
    (orderNumber: string, reason: string, contactId: number, settlementDate?: string | null) => {
      const key = getInteractionKey(orderNumber, contactId);
      const updatedInteraction = {
        ...interactions[key],
        orderNumber,
        contactId,
        propertyAddress: "",
        type: "",
        note: "",
        skipped: true,
        skipReason: reason
      };
      setInteractions((prev) => ({
        ...prev,
        [key]: updatedInteraction
      }));

      saveSkippedInteraction(orderNumber, contactId, reason, undefined, settlementDate);
    },
    [interactions, saveSkippedInteraction]
  );

  const savePreInstructions = useCallback(
    async (orderNumber: string, instructions: string, contactId: number, settlementDate?: string | null) => {
      const key = getInteractionKey(orderNumber, contactId);

      // Wait for any pending save for this key to complete first
      const pendingPromise = pendingSaveRef.current[key];
      if (pendingPromise) {
        await pendingPromise;
      }

      // Check saved ID ref first (most up-to-date), then existing interactions, then current state
      const existing = existingInteractions[key] || existingSkippedInteractions[key];
      const currentInteraction = interactions[key];
      const interactionId = savedIdsRef.current[key] || existing?.id || currentInteraction?.id || 0;

      const payload = {
        interaction: {
          id: interactionId,
          orderNumber,
          contactId,
          typeId: existing?.typeId || currentInteraction?.typeId || null,
          notes: existing?.notes || currentInteraction?.note || "",
          preInteractionInstructions: instructions,
          interactionDate: existing?.interactionDate || currentInteraction?.interactionDate || null,
          clientCultivatorId: existing?.clientCultivatorId || currentInteraction?.clientCultivatorId || userId || 0,
          interactionCategory: settlementDate ? "Closed" : "Received",
          interactionReasonId: settlementDate ? 6 : 5
        },
        colleagueIds: []
      };

      const savePromise = (async () => {
        try {
          const response = await axios.post("/api/clientphonebook/contactInteractions/Upsert", payload);

          const isNewInteraction = interactionId === 0;
          if (response.data && isNewInteraction) {
            // Store the new ID immediately in the ref for future saves
            savedIdsRef.current[key] = response.data;

            const newInteractionData = {
              id: response.data,
              orderNumber,
              contactId,
              typeId: currentInteraction?.typeId || null,
              notes: currentInteraction?.note || "",
              preInteractionInstructions: instructions,
              interactionDate: currentInteraction?.interactionDate || null,
              clientCultivatorId: userId || 0
            };

            setExistingInteractions((prev) => ({
              ...prev,
              [key]: newInteractionData
            }));

            setInteractions((prev) => ({
              ...prev,
              [key]: {
                ...prev[key],
                id: response.data
              }
            }));

            return response.data as number;
          }
          return interactionId || null;
        } catch (err) {
          return null;
        } finally {
          delete pendingSaveRef.current[key];
        }
      })();

      pendingSaveRef.current[key] = savePromise;
      await savePromise;
    },
    [userId, existingInteractions, existingSkippedInteractions, interactions, interactionCategory]
  );

  const handlePreInstructionsChange = useCallback(
    (orderNumber: string, instructions: string, contactId: number, settlementDate?: string | null) => {
      const key = getInteractionKey(orderNumber, contactId);
      const timeoutKey = `pre-${key}`;

      if (saveTimeoutRef.current[timeoutKey]) {
        clearTimeout(saveTimeoutRef.current[timeoutKey]);
      }

      saveTimeoutRef.current[timeoutKey] = setTimeout(() => {
        savePreInstructions(orderNumber, instructions, contactId, settlementDate);
        delete saveTimeoutRef.current[timeoutKey];
      }, 1000);
    },
    [savePreInstructions]
  );

  const handleUndoInteraction = useCallback(
    async (orderNumber: string, contactId: number) => {
      const key = getInteractionKey(orderNumber, contactId);
      const interaction = interactions[key];
      const isSkipped = interaction?.skipped;

      if (isSkipped && existingSkippedInteractions[key]) {
        try {
          const skippedId = existingSkippedInteractions[key].id;
          await axios.delete(`/api/clientphonebook/contactInteractions/delete/${skippedId}`);
          const newSkippedInteractions = { ...existingSkippedInteractions };
          delete newSkippedInteractions[key];
          setExistingSkippedInteractions(newSkippedInteractions);
        } catch (err) {}
      } else if (interaction?.id && existingInteractions[key]) {
        try {
          await axios.delete(`/api/clientphonebook/contactInteractions/delete/${interaction.id}`);
          const newExistingInteractions = { ...existingInteractions };
          delete newExistingInteractions[key];
          setExistingInteractions(newExistingInteractions);
        } catch (err) {}
      }

      const newInteractions = { ...interactions };
      delete newInteractions[key];
      setInteractions(newInteractions);
    },
    [interactions, existingInteractions, existingSkippedInteractions]
  );

  const handleClosedConfirmationChange = useCallback(
    async (orderNumber: string, isConfirmed: boolean) => {
      try {
        if (isConfirmed) {
          await axios.post("/api/clientphonebook/confirmedClosedOrders/confirm", JSON.stringify(orderNumber), {
            headers: { "Content-Type": "application/json" }
          });
        } else {
          await axios.delete(`/api/clientphonebook/confirmedClosedOrders/${encodeURIComponent(orderNumber)}`);
        }
      } catch (err) {}
    },
    []
  );

  return {
    interactions,
    setInteractions,
    handleInteractionTypeChange,
    handleInteractionNoteChange,
    handlePreInstructionsChange,
    handleSkipWithReason,
    handleUndoInteraction,
    handleClosedConfirmationChange,
    setExistingInteractions,
    setExistingSkippedInteractions
  };
};
